import React, { useRef, forwardRef, useImperativeHandle } from "react";
import { View } from "react-native";
import SignatureCanvas from "react-signature-canvas";
import { Button, ButtonText } from "ui/gluestack/button";

interface SignaturePadProps {
  width?: number;
  height?: number;
  strokeColor?: string;
  strokeWidth?: number;
  backgroundColor?: string;
  onSignatureChange?: (hasSignature: boolean) => void;
}

export interface SignaturePadRef {
  exportToPNG: () => Promise<string | null>;
  clear: () => void;
}

export const SignaturePad = forwardRef<SignaturePadRef, SignaturePadProps>(
  (
    {
      strokeColor = "#000000",
      strokeWidth = 2,
      backgroundColor,
      onSignatureChange,
    },
    ref,
  ) => {
    const signatureRef = useRef<SignatureCanvas>(null);

    const handleBegin = () => {
      onSignatureChange?.(true);
    };

    const clear = () => {
      signatureRef.current?.clear();
      onSignatureChange?.(false);
    };

    const exportToPNG = async (): Promise<string | null> => {
      if (!signatureRef.current) return null;

      // Check if canvas is empty
      if (signatureRef.current.isEmpty()) {
        return null;
      }

      // Get the signature as a base64 PNG data URL
      return signatureRef.current.toDataURL("image/png");
    };

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      exportToPNG,
      clear,
    }));

    return (
      <View
        style={{
          borderWidth: 1,
          borderColor: "#D1D5DB",
          borderRadius: 8,
          overflow: "hidden",
        }}
      >
        <SignatureCanvas
          ref={signatureRef}
          onBegin={handleBegin}
          penColor={strokeColor}
          minWidth={strokeWidth}
          maxWidth={strokeWidth + 1}
          backgroundColor={backgroundColor}
          canvasProps={{
            width: 300,
            height: 300,
            style: {
              width: "100%",
              height: "100%",
            },
          }}
        />
      </View>
    );
  },
);

SignaturePad.displayName = "SignaturePad";

export { SignaturePad as default };
export type { SignaturePadProps };
